import { DynamicElementMixin } from "%/dynamic-components/render-engine/mixins/production/dynamic-element-mixin";
import LayoutWidthHeightOptions from "%/dynamic-components/render-engine/bundles/layout-width-height-options";
import AppearanceOptions from "%/dynamic-components/render-engine/bundles/appearance-options";
import AnimationsOptions from "%/dynamic-components/render-engine/bundles/animations-options";
import BorderOptions from "%/dynamic-components/render-engine/bundles/border-options";
import SpacingOptions from "%/dynamic-components/render-engine/bundles/spacing-options";
import LayoutWidth from "%/dynamic-components/render-engine/options/layout-options/layout-width";
import LayoutHeight from "%/dynamic-components/render-engine/options/layout-options/layout-height";
import ObjectFit from "%/dynamic-components/render-engine/options/image-options/object-fit";
import ImageAltText from "%/dynamic-components/render-engine/options/image-options/image-alt-text";
import ImageShape from "%/dynamic-components/render-engine/options/image-options/image-shape";
import ObjectPosition from "%/dynamic-components/render-engine/options/image-options/object-position";
import BorderRadius from "%/dynamic-components/render-engine/options/border-options/border-radius";
import ImageFillBackground from "%/dynamic-components/render-engine/options/image-options/image-fill-background";
import BorderSides from "%/dynamic-components/render-engine/options/border-options/border-sides";
import Padding from "%/dynamic-components/render-engine/options/spacing-options/padding";
import BaseOption from "%/dynamic-components/render-engine/classes/base-option";
import Margins from "%/dynamic-components/render-engine/options/spacing-options/margins";
import DynamicComponentError from "%/errors/dynamic-component-error";
import MaxWidth from "%/dynamic-components/render-engine/options/spacing-options/max-width";
import MaxHeight from "%/dynamic-components/render-engine/options/spacing-options/max-height";
import MinWidth from "%/dynamic-components/render-engine/options/spacing-options/min-width";
import MinHeight from "%/dynamic-components/render-engine/options/spacing-options/min-height";
import OpenInNewTab from "%/dynamic-components/render-engine/options/open-in-new-tab";
import LinkAction from "%/dynamic-components/render-engine/options/click-actions/link-action";
import { GestureActionsMixin } from "%/dynamic-component-editor/standard-elements/click-actions/gesture-actions-mixin";
import LinkType from "%/dynamic-components/render-engine/options/link-type";
import { resolveContextualPageValue } from "%/dynamic-components/render-engine/options/click-actions/contextual-page-value";
import HrefOption from "%/dynamic-components/render-engine/options/url-options/href-option";
import Crop from "%/dynamic-components/render-engine/options/image-options/crop";
import Label from "%/dynamic-components/render-engine/options/url-options/label";
import BackgroundOptions from "%/dynamic-components/render-engine/bundles/background-options";
import BackgroundGradient from "%/dynamic-components/render-engine/options/background-options/background-gradient";
import BackgroundColor from "%/dynamic-components/render-engine/options/background-options/background-color";
import EffectsOptions from "%/dynamic-components/render-engine/bundles/effects-options";
import BoxShadow from "%/dynamic-components/render-engine/options/box-shadow-options/box-shadow";
import Blur from "%/dynamic-components/render-engine/options/layout-options/blur";
import Brightness from "%/dynamic-components/render-engine/options/image-options/brightness";
import Contrast from "%/dynamic-components/render-engine/options/image-options/contrast";
import Grayscale from "%/dynamic-components/render-engine/options/image-options/grayscale";
import HueRotate from "%/dynamic-components/render-engine/options/image-options/hue-rotate";
import Invert from "%/dynamic-components/render-engine/options/image-options/invert";
import ImageOpacity from "%/dynamic-components/render-engine/options/image-options/opacity";
import Saturate from "%/dynamic-components/render-engine/options/image-options/saturate";
import Sepia from "%/dynamic-components/render-engine/options/image-options/sepia";
import BlendMode from "%/dynamic-components/render-engine/options/image-options/blend-mode";
import Rendering from "%/dynamic-components/render-engine/options/image-options/rendering";
import ClipPath from "%/dynamic-components/render-engine/options/image-options/clip-path";
import AnimationEffectsOptions from "%/dynamic-components/render-engine/bundles/animation-effects-options";

export const StandardImageElementMixin = {
  extends: DynamicElementMixin,
  mixins: [GestureActionsMixin],
  gestureOption: LinkAction,
  elementPanelSettings: {
    displayIcon: "image",
    elementType: "element",
  },
  optionBundles: [
    new LayoutWidthHeightOptions({
      "default.layout-width": 300,
    }),
    new AppearanceOptions({}, {
      "border-radius": false,
      "blur": true,
      "opacity": true,
    }),
    AnimationsOptions,
    AnimationEffectsOptions,
    BorderOptions,
    SpacingOptions,
    new EffectsOptions({}, {
      "text-shadow": true,
    }),
    new BackgroundOptions({}, {
      "background-image": true,
    }),
  ],
  options: [
    LinkAction,
    new HrefOption(undefined, true),
    new Label(undefined, true),
    new LinkType(undefined, true),
    new OpenInNewTab(undefined, true),
  ],
  data() {
    return {
      LayoutWidth,
      LayoutHeight,
      ObjectFit,
      ImageShape,
      ObjectPosition,
      BorderRadius,
      ImageFillBackground,
      BorderOptions,
      MinHeight,
      MinWidth,
      MaxHeight,
      MaxWidth,
      Crop,
      BackgroundColor,
      BackgroundGradient,
      BlendMode,
      Rendering,
    };
  },
  computed: {
    target() {
      return this.getOptionValue(LinkAction)?.openInNewTab ? "_blank" : "_self";
    },
    fallbackImageSrc() {
      throw new DynamicComponentError("Element must implement fallbackImageSrc computed property when extending base image element");
    },
    // Entity-page link seam — same contract as text-element-mixin's
    // entityPageHref: entity-bound subclasses (DynamicAppImageElement)
    // override this to their bound entity's resolved display-page URL
    // (`link-to-entity-page` option); when set it wins over an authored
    // LinkAction below. Base image elements have no entity context — null.
    entityPageHref() {
      return null;
    },
    imageHref() {
      if (this.entityPageHref) {
        return this.entityPageHref;
      }
      const linkValues = this.getOptionValue(LinkAction);
      if (!linkValues || this.gestureOwnsTheClick) {
        return "";
      }
      const urlType = linkValues.linkType;
      let urlValue = linkValues.value;

      switch (urlType) {
        case LinkType.LINK_TYPE_HYPERLINK:
          return urlValue;
        case LinkType.LINK_TYPE_PAGE:
          urlValue = resolveContextualPageValue(urlValue);
          if (urlValue === null || urlValue === undefined) {
            return;
          }
          if (urlValue.startsWith("/")) {
            urlValue = urlValue.substring(1);
          }
          return `${this.links.web}/${urlValue}`;
        case LinkType.LINK_TYPE_STORY:
          return `${this.links.web}/story/${urlValue}`;
        case LinkType.LINK_TYPE_HASHTAG:
          return `${this.links.web}/stories/${urlValue}`;
        default:
          throw new DynamicComponentError("Url type not found", {
            urlType,
            urlValue,
          });
      }
    },
    imageSrc() {
      throw new DynamicComponentError("Element must implement imageSrc computed property when extending base image element");
    },
    imageMediaUnresolvable() {
      return false;
    },
    imageAltText() {
      return this.getOptionValue(ImageAltText) || "";
    },
    myBorderRadius() {
      return this.getOptionValue(BorderRadius);
    },
    myBorderSides() {
      return this.getOptionValue(BorderSides);
    },
    myClipPath() {
      return this.getOptionValue(ClipPath);
    },
    myHeight() {
      const heightValue = this.getOptionValue(LayoutHeight);
      if (heightValue === "hug") {
        return "auto";
      }
      return heightValue;
    },
    myWidth() {
      const widthValue = this.getOptionValue(LayoutWidth);
      if (widthValue === "hug") {
        return "auto";
      }
      return widthValue;
    },
    imageStyleOverrides() {
      return {
        borderRadius: this.myBorderRadius,
        paddingTop: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "top") || "none",
        paddingBottom: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "bottom") || "none",
        paddingLeft: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "left") || "none",
        paddingRight: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "right") || "none",
        marginTop: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "top") || "none",
        marginBottom: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "bottom") || "none",
        marginLeft: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "left") || "none",
        marginRight: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "right") || "none",
        paddingHoverTop: this.getOptionValue(Padding, BaseOption.$states.HOVER, "top") || "none",
        paddingHoverBottom: this.getOptionValue(Padding, BaseOption.$states.HOVER, "bottom") || "none",
        paddingHoverLeft: this.getOptionValue(Padding, BaseOption.$states.HOVER, "left") || "none",
        paddingHoverRight: this.getOptionValue(Padding, BaseOption.$states.HOVER, "right") || "none",
        marginHoverTop: this.getOptionValue(Margins, BaseOption.$states.HOVER, "top") || "none",
        marginHoverBottom: this.getOptionValue(Margins, BaseOption.$states.HOVER, "bottom") || "none",
        marginHoverLeft: this.getOptionValue(Margins, BaseOption.$states.HOVER, "left") || "none",
        marginHoverRight: this.getOptionValue(Margins, BaseOption.$states.HOVER, "right") || "none",
        boxShadow: this.getOptionValue(BoxShadow),
        boxShadowHover: this.getOptionValue(BoxShadow, BaseOption.$states.HOVER),
        backgroundColor: this.getOptionValue(BackgroundColor),
        backgroundGradient: this.getOptionValue(BackgroundGradient),
      };
    },
    imageFilterEffects() {
      return {
        blur: this.getOptionValue(Blur),
        brightness: this.getOptionValue(Brightness),
        contrast: this.getOptionValue(Contrast),
        grayscale: this.getOptionValue(Grayscale),
        "hue-rotate": this.getOptionValue(HueRotate),
        invert: this.getOptionValue(Invert),
        opacity: this.getOptionValue(ImageOpacity),
        saturate: this.getOptionValue(Saturate),
        sepia: this.getOptionValue(Sepia),
      };
    },
  },
};
